import { logError } from "@lib/logger";
import { IEventDataSource, IGameOrganizer } from "@/types/interfaces";
import { BigBashEvent, Game, GameSelectionEventDetails } from "@/types/types";
import EventConflictError from "@/utils/EventConflictError";
import { QueueAndRunRequests } from "@infrastructure/queueAndRunRequests";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { GameOrderPoller } from "./GameOrderPoller";

@customElement("moderate-event-page")
export class ModerateEventPage extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
		    .back-btn {
				position: absolute;
				top: 20px;
				left: 20px;
				color: black;
				border: var(--border-default);
				border-radius: 50%;
				height: 40px;
				width: 40px;
				cursor: pointer;
				text-decoration: none;
				font-size: 1rem;
				transition: all 0.3s ease;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			.container {
				max-width: 1200px;
				min-width: 1000px;
				border: var(--border-default);
				border-radius: var(--border-radius);
				background: white;
				box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
				padding: 16px;
				display: flex;
				flex-direction: column;
				gap: 16px;
				align-items: center;
				justify-content: center;
			}

			.container-middle {
				display: flex;
				justify-content: space-between;
				align-items: center;
			}

			.container-bottom {
				display: flex;
				flex-direction: column;
				gap: 16px;
				background: var(--color-pink);
			}

			.container > * {
				width: 100%;
			}
		`
	];

	@state() private event: BigBashEvent | null = null;
	@state() private isLoading = true;
	@state() private allGames: Game[] = [];
	@state() private isSaveInFlight = false;
	@property({}) eventId: string;
	@property({ type: Object }) eventDataSource: IEventDataSource;
	@property({ type: Object }) gameOrganizer: IGameOrganizer;
	private gameOrderPoller: GameOrderPoller | null = null;
	private queuedRequestResolvers: Array<() => void> = [];

	constructor(eventDataSource: IEventDataSource, eventId: string, gameOrganizer: IGameOrganizer) {
		super();
		this.eventDataSource = eventDataSource;
		this.eventId = eventId;
		this.gameOrganizer = gameOrganizer;
		this.addEventListener("selection-change", this._handleGameSelectionChange.bind(this));
		this.addEventListener("points-reset", this._tryToLoadEventData.bind(this));
	}

	async connectedCallback(): Promise<void> {
		super.connectedCallback();
		await this._tryToLoadEventData();
		await this._tryToLoadAllGames();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.gameOrderPoller?.stop();
		this.gameOrderPoller = null;
	}

	private readonly queueAndRunRequests: QueueAndRunRequests = async <TResult,>(operation: () => Promise<TResult>): Promise<TResult> => {
		this.gameOrderPoller?.invalidateInFlightPollRequestAndResetSchedule();
		if (this.isSaveInFlight) {
			await new Promise<void>(resolve => this.queuedRequestResolvers.push(resolve));
		}
		this.isSaveInFlight = true;
		try {
			return await operation();
		} finally {
			this.isSaveInFlight = false;
			this.queuedRequestResolvers.shift()?.();
		}
	};

	private async _handleGameSelectionChange(event: CustomEvent<GameSelectionEventDetails>) {
		if (!this.event) return;
		const details = event.detail;

		const hasValidPreviousGame = details.previousValue &&
			details.previousLabel &&
			details.previousValue !== "";

		if (hasValidPreviousGame) {
			const confirmMessage = `Sie ersetzen das Spiel "${details.previousLabel}" durch "${details.newLabel}". ` +
				`Bereits vergebene Punkte werden nicht automatisch gelöscht - wenn nötig, bitte vorher entfernen. Fortfahren?`;

			if (!confirm(confirmMessage)) {
				this.requestUpdate();
				return;
			}
		}

		try {
			const updatedEvent = await this.queueAndRunRequests(() => this._updateEventWithNewGame(details));
			this._setEvent(updatedEvent);
			this.requestUpdate();
		} catch (error) {
			if (error instanceof EventConflictError) {
				alert("Das Event wurde inzwischen von einem anderen Gerät geändert. Die aktuellen Daten werden neu geladen.");
				await this._tryToLoadEventData();
				return;
			}

			logError(error.message);
			alert("Fehler beim Aktualisieren des Spiels. Bitte versuchen Sie es erneut.");

			this.requestUpdate();
		}
	}

	private async _updateEventWithNewGame(details: GameSelectionEventDetails): Promise<BigBashEvent> {
		if (!this.event) throw new Error("No event available");
		return await this.eventDataSource.swapGame(this.event.id, details.previousValue, details.newValue);
	}

	private async _tryToLoadEventData() {
		this.isLoading = true;
		try {
			const event = await this.eventDataSource.getEventById(this.eventId);
			this._handleEventResult(event);
		} catch (error) {
			this._handleFetchError(error);
		} finally {
			this.isLoading = false;
		}
	}

	private async _tryToLoadAllGames() {
		this.isLoading = true;
		try {
			const games = await this.eventDataSource.getAllGames();
			this._handleAllGamesResult(games);
		} catch (error) {
			this._handleFetchError(error);
		} finally {
			this.isLoading = false;
		}
	}

	private _handleFetchError(error: unknown): void {
		logError("Error fetching event:", error);
		alert("Failed to load event. Please try again later.");
	}

	private _handleEventResult(event: BigBashEvent): void {
		this.event = event;
		this.gameOrderPoller?.stop();
		this.gameOrderPoller = new GameOrderPoller(
			(): void => { },
			(updatedEvent: BigBashEvent): void => { this._setEvent(updatedEvent); },
			event,
			this.eventDataSource
		);
		this.gameOrderPoller.start();
	}

	private _handleAllGamesResult(games: Game[]) {
		this.allGames = games;
	}

	private _setEvent(event: BigBashEvent): void {
		this.event = event;
		this.gameOrderPoller?.setEvent(event);
	}

	private readonly handleBackClick = (event: BigBashEvent) => {
		window.history.pushState({}, '', '/host/events');
	};

	render() {
		if (this.isLoading || !this.event) {
			return html`
			    <a href="/host/events" class="back-btn" @click="${this.handleBackClick}">←</a>
				<div class="container">
					<div style="text-align: center; padding: 60px;">
						Loading events...
					</div>
				</div>
			`;
		}

		return html`
		    <a href="/host/events" class="back-btn" @click="${this.handleBackClick}">←</a>
			<div class="container">
				<div class="container-top">
					<h1 class="text-headline">Ablauf ${this.event?.name}</h1>
					<p class="text-body">
						Spiele starten oder bearbeiten
					</p>
				</div>

				<div class="container-middle">
				</div>

				<div class="container-bottom">
					<moderate-event-table 
					.gameOrganizer="${this.gameOrganizer}"
					.event="${this.event}" 
					.eventDataSource="${this.eventDataSource}"
					.allGames="${this.allGames}"
					></moderate-event-table>
				</div>
			</div>
		`;
	}
}

export default ModerateEventPage;